import React, { useEffect, useState, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Button } from '@nextui-org/react';
import { COLORS } from 'common/src/utils/colors';
import { MarketData } from 'common/src/utils/types';
import { BitacoraPriceData } from 'common/src/api/market_comments';
import useUserStore from '../../stores/useUserStore';
import { getChartThemeColors, tooltipContainerStyle, tooltipTitleStyle, tooltipLabelStyle, tooltipValueStyle } from '../../utils/chartTheme';
import VarietyFilter from '../filters/VarietyFilter';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import { useTranslation } from '../../hooks/useTranslation';
import BrandDetailModal from '../../modals/BrandDetailModal';
import {
  BitacoraMode,
  buildBitacoraSeries,
  isBitacoraSeries,
  renderBitacoraTooltip,
} from './bitacoraSeries';
import ChartDownloadButton from './ChartDownloadButton';
import { buildExporterSeries, isExporterSeries } from './exporterSeries';

interface props {
  data: MarketData[];
  selectedCurrency: 'RMB' | 'USD';
  priceView: 'kg' | 'box';
  topExportersData: MarketData[];
  boxSize?: number;
  visualization?: 'chart' | 'table';
  hideExporterLegend?: boolean; // Hide exporter series from legend (for importers app)
  separationType?: 'none' | 'brand'; // Type of separation for user exporter data
  selectedBrands?: string[]; // Selected brands to show when separationType is 'brand'
  bitacoraData?: BitacoraPriceData[]; // Bitácora prices to overlay on chart
  bitacoraMode?: BitacoraMode; // Un punto promedio, o uno por cliente o por etiqueta
  exporterBrandKeys?: string[] | null; // Etiquetas de las exportadoras seleccionadas a separar
}

const DailyMarketChart: React.FC<props> = ({
  data,
  selectedCurrency,
  priceView,
  topExportersData,
  boxSize = 5,
  visualization = 'chart',
  hideExporterLegend = false,
  separationType = 'none',
  selectedBrands = [],
  bitacoraData = [],
  bitacoraMode = 'average',
  exporterBrandKeys = null,
}) => {
  const { t } = useTranslation('market');
  const [selectedVariety, setSelectedVariety] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCaliber, setSelectedCaliber] = useState('');
  const { userProfile, darkMode } = useUserStore();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartColors = getChartThemeColors(darkMode);
  const userExporter = userProfile?.exporter;

  const handlePointClick = (caliber: string) => {
    setSelectedCaliber(caliber);
    setIsModalOpen(true);
  };

  // Colors for brands when separationType is 'brand'
  const brandColors = [
    '#4CAF50', // green
    '#FF9800', // orange
    '#9C27B0', // purple
    '#00BCD4', // cyan
    '#FF5722', // deep orange
    '#673AB7', // deep purple
    '#009688', // teal
    '#FFC107', // amber
  ];

  useEffect(() => {
    const loadData = async () => {
      // Solo establecer la variedad si no hay una seleccionada y hay datos
      if (!selectedVariety && data.length > 0) {
        const varietyCounts = data.reduce(
          (acc, item) => {
            acc[item.variety] = (acc[item.variety] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>
        );

        // Only proceed if we have varieties
        if (Object.keys(varietyCounts).length > 0) {
          const mostCommonVariety = Object.keys(varietyCounts).reduce((a, b) =>
            varietyCounts[a] > varietyCounts[b] ? a : b
          );

          setSelectedVariety(mostCommonVariety);
        }
      }
    };
    loadData();
  }, [data, selectedVariety]);

  const prepareChartData = (data: MarketData[]) => {
    const grouped = data?.reduce(
      (acc, item) => {
        const baseSize = item.size;

        if (!acc[baseSize]) {
          acc[baseSize] = {
            prices: [],
            userExporterPrices: [],
            userExporterPricesByBrand: {}, // Para separación por etiqueta
            dark: 0,
            light: 0,
          };
        }
        const priceKg = item.price_kg;

        acc[baseSize].prices.push(priceKg);

        // Agregar precios del usuario
        if (item.exporter === userExporter?.excel_name) {
          acc[baseSize].userExporterPrices.push(priceKg);

          // Si hay separación por etiqueta, también agrupar por brand
          if (separationType === 'brand' && item.brand) {
            if (!acc[baseSize].userExporterPricesByBrand[item.brand]) {
              acc[baseSize].userExporterPricesByBrand[item.brand] = [];
            }
            acc[baseSize].userExporterPricesByBrand[item.brand].push(priceKg);
          }
        }

        if (item?.color?.endsWith('D')) {
          acc[baseSize].dark++;
        } else if (item?.color?.endsWith('L')) {
          acc[baseSize].light++;
        }
        return acc;
      },
      {} as Record<
        string,
        {
          prices: number[];
          userExporterPrices: number[];
          userExporterPricesByBrand: Record<string, number[]>;
          dark: number;
          light: number;
        }
      >
    );

    const customSort = (a: string, b: string) => {
      const order = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
      const aIndex = order.indexOf(a);
      const bIndex = order.indexOf(b);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    };
    const calibers = Object.keys(grouped).sort(customSort);

    // Un punto por exportadora seleccionada, no un promedio de todas.
    const exporterSeries = buildExporterSeries({
      records: topExportersData,
      categories: calibers,
      categoryOf: (item) =>
        item.variety === selectedVariety ? item.size : null,
      marketPrices: Object.fromEntries(
        calibers.map((c) => [c, grouped[c].prices])
      ),
      excludeExporter: userExporter?.excel_name,
      brandKeys: exporterBrandKeys,
      noBrandLabel: t('Sin etiqueta'),
    });

    const boxPlotData = calibers.map((c, caliberIndex) => {
      const allPrices = grouped[c].prices;
      const sortedPrices = allPrices.sort((a, b) => a - b);
      const q1 = sortedPrices[Math.floor(sortedPrices.length / 4)];
      const median = sortedPrices[Math.floor(sortedPrices.length / 2)];
      const q3 = sortedPrices[Math.floor((3 * sortedPrices.length) / 4)];
      const min = sortedPrices[0];
      const max = sortedPrices[sortedPrices.length - 1];
      const userExporterAvg =
        grouped[c].userExporterPrices.length > 0
          ? grouped[c].userExporterPrices.reduce((a, b) => a + b, 0) /
            grouped[c].userExporterPrices.length
          : null;

      // Calculate XSUR percentile for this specific caliber
      let userExporterPercentile = null;
      if (userExporterAvg !== null) {
        const lowerPrices = sortedPrices.filter(
          (price) => price <= userExporterAvg
        );
        userExporterPercentile =
          (lowerPrices.length / sortedPrices.length) * 100;
      }

      // Calculate brands data for tooltip (when separationType === 'brand')
      const brandsData = selectedBrands.map((brand) => {
        const brandPrices = grouped[c]?.userExporterPricesByBrand[brand] || [];
        const brandAvg =
          brandPrices.length > 0
            ? brandPrices.reduce((a, b) => a + b, 0) / brandPrices.length
            : null;

        let brandPercentile = null;
        if (brandAvg !== null) {
          const lowerPrices = sortedPrices.filter((price) => price <= brandAvg);
          brandPercentile = (lowerPrices.length / sortedPrices.length) * 100;
        }

        return {
          brand,
          avg: brandAvg,
          percentile: brandPercentile,
          records: brandPrices.length,
        };
      });

      return {
        x: c,
        y: [min, q1, median, q3, max],
        yBox: [min, q1, median, q3, max],
        userExporterAvg,
        userExporterAvgBox: userExporterAvg,
        userExporterPercentile,
        totalRecords: allPrices.length,
        userExporterRecords: grouped[c].userExporterPrices.length,
        exportersData: exporterSeries.map((s) => ({
          name: s.name,
          color: s.color,
          ...s.data[caliberIndex],
        })),
        brandsData, // Add brands data for tooltip
        separationType, // Add separationType for tooltip logic
      };
    });

    const darkLightPercentages = calibers.map((c) => {
      const total = grouped[c].dark + grouped[c].light;
      return {
        dark: ((grouped[c].dark / total) * 100).toFixed(1),
        light: ((grouped[c].light / total) * 100).toFixed(1),
      };
    });

    // Build series based on separationType
    const series: any[] = [
      {
        name: t('MERCADO'),
        type: 'boxPlot',
        data: boxPlotData,
      },
    ];

    // Add user exporter series
    if (separationType === 'none') {
      // Single user exporter series (green point)
      series.push({
        name: userExporter?.excel_name?.toUpperCase(),
        type: 'scatter',
        data: boxPlotData.map((item) => ({
          x: item.x,
          y: item.userExporterAvg,
          userExporterPercentile: item.userExporterPercentile,
        })),
      });
    } else if (separationType === 'brand' && selectedBrands.length > 0) {
      // Multiple brand series (one per selected brand)
      selectedBrands.forEach((brand, index) => {
        const brandData = calibers.map((caliber) => {
          const brandPrices =
            grouped[caliber]?.userExporterPricesByBrand[brand] || [];
          const brandAvg =
            brandPrices.length > 0
              ? brandPrices.reduce((a, b) => a + b, 0) / brandPrices.length
              : null;

          // Calculate percentile for this brand in this caliber
          let brandPercentile = null;
          if (brandAvg !== null) {
            const allPrices = grouped[caliber]?.prices || [];
            const sortedPrices = allPrices.sort((a, b) => a - b);
            const lowerPrices = sortedPrices.filter(
              (price) => price <= brandAvg
            );
            brandPercentile = (lowerPrices.length / sortedPrices.length) * 100;
          }

          return {
            x: caliber,
            y: brandAvg,
            brandPercentile: brandPercentile,
            brandRecords: brandPrices.length,
            brandName: brand,
          };
        });

        series.push({
          name: [userExporter?.excel_name, brand].filter(Boolean).join(' · ').toUpperCase(),
          type: 'scatter',
          data: brandData,
          color: brandColors[index % brandColors.length],
          isBrandSeries: true,
        });
      });
    }

    series.push(...exporterSeries);

    // Puntos de Bitácora: promedio ponderado por bultos, o uno por cliente o por etiqueta.
    series.push(
      ...buildBitacoraSeries({
        records: bitacoraData,
        categories: calibers,
        categoryOf: (b) =>
          b.variety &&
          b.caliber &&
          b.variety.toUpperCase() === selectedVariety.toUpperCase()
            ? b.caliber.toUpperCase()
            : null,
        mode: bitacoraMode,
        label: t('Bitácora'),
        noClientLabel: t('Sin cliente'),
        noLabelLabel: t('Sin etiqueta o varias'),
      })
    );

    const result = {
      categories: calibers,
      series,
      darkLightPercentages,
    };

    return result;
  };

  console.log('selectedVariety', selectedVariety);
  const filteredData = data.filter((item) => item.variety === selectedVariety);

  const { categories, series, darkLightPercentages } =
    prepareChartData(filteredData);

  // Función para exportar a Excel
  const exportToExcel = () => {
    const excelData: any[][] = [];
    const unit = priceView === 'kg' ? '/kg' : `/${boxSize}kg box`;
    const currency = selectedCurrency === 'USD' ? 'USD' : 'RMB';

    // Encabezados
    const headers = [t('Métrica'), ...categories];
    excelData.push(headers);

    // Fila de registros
    excelData.push([
      t('Registros'),
      ...categories.map((_, index) => series[0].data[index].totalRecords),
    ]);

    // Filas de estadísticas del mercado
    excelData.push([
      t('Mínimo'),
      ...categories.map((_, index) => series[0].data[index].y[0]),
    ]);
    excelData.push([
      t('Q1'),
      ...categories.map((_, index) => series[0].data[index].y[1]),
    ]);
    excelData.push([
      t('Mediana'),
      ...categories.map((_, index) => series[0].data[index].y[2]),
    ]);
    excelData.push([
      t('Q3'),
      ...categories.map((_, index) => series[0].data[index].y[3]),
    ]);
    excelData.push([
      t('Máximo'),
      ...categories.map((_, index) => series[0].data[index].y[4]),
    ]);

    // Filas del exportador si existe
    if (userExporter && separationType === 'brand') {
      // Una fila por etiqueta propia, como en el gráfico
      series
        .filter((s) => s.isBrandSeries)
        .forEach((brandSerie) => {
          excelData.push([
            brandSerie.name,
            ...brandSerie.data.map((point) =>
              point.y !== null && point.y !== undefined ? point.y : '-'
            ),
          ]);
          excelData.push([
            `${brandSerie.name} ${t('Percentil')}`,
            ...brandSerie.data.map((point) =>
              point.brandPercentile !== null && point.brandPercentile !== undefined
                ? `${point.brandPercentile.toFixed(0)}%`
                : '-'
            ),
          ]);
        });
    } else if (userExporter) {
      excelData.push([
        userExporter.excel_name,
        ...categories.map((_, index) => {
          const userExporterAvg = series[0].data[index].userExporterAvg;
          return userExporterAvg !== null && userExporterAvg !== undefined
            ? userExporterAvg
            : 'N/A';
        }),
      ]);
      excelData.push([
        t('Percentil'),
        ...categories.map((_, index) => {
          const percentile = series[0].data[index].userExporterPercentile;
          return percentile !== null && percentile !== undefined
            ? `${percentile.toFixed(0)}%`
            : 'N/A';
        }),
      ]);
    }

    // Una fila por exportadora seleccionada
    series.filter(isExporterSeries).forEach((exporterSerie) => {
      excelData.push([
        exporterSerie.name,
        ...exporterSerie.data.map((point) =>
          point.y !== null ? point.y : 'N/A'
        ),
      ]);
    });

    // Crear libro Excel
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Ajustar anchos de columna
    ws['!cols'] = [{ wch: 30 }, ...categories.map(() => ({ wch: 12 }))];

    // Exportar
    XLSX.utils.book_append_sheet(wb, ws, 'Dispersión por calibre');
    XLSX.writeFile(wb, `Dispersión_Calibre_${selectedVariety}.xlsx`);
  };

  // Componente TableView
  const TableView = () => {
    const unit = priceView === 'kg' ? '/kg' : `/${boxSize}kg box`;
    const currency = selectedCurrency === 'USD' ? 'USD' : 'RMB';
    const sign = selectedCurrency === 'USD' ? '$' : '¥';

    return (
      <div className='mt-4'>
        <div className='overflow-x-auto'>
          <table className='min-w-full border-collapse'>
            <thead>
              <tr className='bg-default-100'>
                <th className='border px-4 py-2 sticky left-0 bg-default-100 z-10'>
                  {t('Métrica')}
                </th>
                {categories.map((caliber) => (
                  <th
                    key={caliber}
                    className='border px-4 py-2 text-center min-w-[100px]'
                  >
                    {caliber}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  {t('Registros')}
                </td>
                {categories.map((caliber, index) => {
                  const dataPoint = series[0].data[index];
                  return (
                    <td key={caliber} className='border px-4 py-2 text-center'>
                      {dataPoint.totalRecords}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  {t('Mínimo')}
                </td>
                {categories.map((caliber, index) => {
                  const dataPoint = series[0].data[index];
                  return (
                    <td key={caliber} className='border px-4 py-2 text-center'>
                      {sign}
                      {dataPoint.y[0].toFixed(2)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  {t('Q1')}
                </td>
                {categories.map((caliber, index) => {
                  const dataPoint = series[0].data[index];
                  return (
                    <td key={caliber} className='border px-4 py-2 text-center'>
                      {sign}
                      {dataPoint.y[1].toFixed(2)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  {t('Mediana')}
                </td>
                {categories.map((caliber, index) => {
                  const dataPoint = series[0].data[index];
                  return (
                    <td key={caliber} className='border px-4 py-2 text-center'>
                      {sign}
                      {dataPoint.y[2].toFixed(2)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  {t('Q3')}
                </td>
                {categories.map((caliber, index) => {
                  const dataPoint = series[0].data[index];
                  return (
                    <td key={caliber} className='border px-4 py-2 text-center'>
                      {sign}
                      {dataPoint.y[3].toFixed(2)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  {t('Máximo')}
                </td>
                {categories.map((caliber, index) => {
                  const dataPoint = series[0].data[index];
                  return (
                    <td key={caliber} className='border px-4 py-2 text-center'>
                      {sign}
                      {dataPoint.y[4].toFixed(2)}
                    </td>
                  );
                })}
              </tr>
              {separationType === 'brand' &&
                series
                  .filter((s) => s.isBrandSeries)
                  .map((brandSerie) => (
                    <tr key={brandSerie.name}>
                      <td className='border px-4 py-2 font-medium sticky left-0 bg-green-50 z-10'>
                        <span className='flex items-center gap-2'>
                          <span
                            className='inline-block h-2.5 w-2.5 rounded-full'
                            style={{ backgroundColor: brandSerie.color }}
                          />
                          {brandSerie.name}
                        </span>
                      </td>
                      {brandSerie.data.map((point) => (
                        <td
                          key={point.x}
                          className='border px-4 py-2 text-center bg-green-50 font-semibold text-green-700'
                        >
                          {point.y !== null && point.y !== undefined
                            ? `${sign}${point.y.toFixed(2)} · ${point.brandPercentile?.toFixed(0) ?? '-'}%`
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
              {userExporter && separationType !== 'brand' && (
                <>
                  <tr>
                    <td className='border px-4 py-2 font-medium sticky left-0 bg-green-50 z-10'>
                      {userExporter.excel_name}
                    </td>
                    {categories.map((caliber, index) => {
                      const userExporterAvg =
                        series[0].data[index].userExporterAvg;
                      return (
                        <td
                          key={caliber}
                          className='border px-4 py-2 text-center bg-green-50 font-semibold text-green-700'
                        >
                          {userExporterAvg !== null &&
                          userExporterAvg !== undefined
                            ? `${sign}${userExporterAvg.toFixed(2)}`
                            : '-'}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium sticky left-0 bg-green-50 z-10'>
                      {t('Percentil')}
                    </td>
                    {categories.map((caliber, index) => {
                      const percentile =
                        series[0].data[index].userExporterPercentile;
                      return (
                        <td
                          key={caliber}
                          className='border px-4 py-2 text-center bg-green-50 font-semibold text-green-700'
                        >
                          {percentile !== null && percentile !== undefined
                            ? `${percentile.toFixed(0)}%`
                            : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </>
              )}
              {series.filter(isExporterSeries).map((exporterSerie) => (
                <tr key={exporterSerie.name}>
                  <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                    <span className='flex items-center gap-2'>
                      <span
                        className='inline-block h-2.5 w-2.5 rounded-full'
                        style={{ backgroundColor: exporterSerie.color }}
                      />
                      {exporterSerie.name}
                    </span>
                  </td>
                  {exporterSerie.data.map((point) => (
                    <td
                      key={point.x}
                      className='border px-4 py-2 text-center font-semibold'
                    >
                      {point.y !== null ? `${sign}${point.y.toFixed(2)}` : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Build dynamic colors array based on series
  const buildColorsArray = () => {
    const colors = [COLORS.cherry]; // Box plot always first

    if (separationType === 'none') {
      colors.push(COLORS.lightGreen); // User exporter (green)
    } else if (separationType === 'brand') {
      // Add brand colors
      selectedBrands.forEach((_, index) => {
        colors.push(brandColors[index % brandColors.length]);
      });
    }

    // Un color por exportadora seleccionada.
    series
      .filter(isExporterSeries)
      .forEach((s) => colors.push(s.color));

    // Un morado por cada serie de Bitácora dibujada.
    series
      .filter(isBitacoraSeries)
      .forEach((s) => colors.push(s.color));

    return colors;
  };

  const chartOptions: ApexOptions = {
    chart: {
      type: 'boxPlot',
      height: 400,
      toolbar: { show: false },
      foreColor: chartColors.foreColor,
      zoom: {
        enabled: false,
      },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const caliber = categories[config.dataPointIndex];

          // Check if user exporter has data for this caliber
          if (userExporter) {
            // Only open modal if clicking on box plot (seriesIndex 0) or user exporter/brand points
            if (
              config.seriesIndex === 0 ||
              (config.seriesIndex > 0 &&
                config.seriesIndex < series.length - 1 &&
                !series[config.seriesIndex]?.isBitacoraSeries &&
                !isExporterSeries(series[config.seriesIndex]))
            ) {
              const hasData =
                series[config.seriesIndex]?.data[config.dataPointIndex]?.y !==
                  null &&
                series[config.seriesIndex]?.data[config.dataPointIndex]?.y !==
                  undefined;
              if (hasData || config.seriesIndex === 0) {
                handlePointClick(caliber);
              }
            }
          }
        },
      },
    },
    colors: buildColorsArray(),

    xaxis: {
      categories: categories,
      title: {
        text: t('Calibre'),
      },
    },
    yaxis: {
      title: {
        text:
          selectedCurrency === 'USD'
            ? `${t('Precio')} (USD/${priceView === 'kg' ? 'kg' : `${boxSize}kg box`})`
            : `${t('Precio')} (RMB/${priceView === 'kg' ? 'kg' : `${boxSize}kg box`})`,
      },
      labels: {
        formatter: function (value) {
          const sign = selectedCurrency === 'USD' ? '$' : '¥';
          return value.toFixed(priceView === 'kg' ? 2 : 0) + ' ' + sign;
        },
      },
    },
    markers: {
      size: Array(series.length)
        .fill(0)
        .map((_, i) => (i === 0 ? 0 : 5)), // 0 for boxPlot, 5 for scatter
      colors: buildColorsArray(),
      hover: {
        size: 7,
      },
    },
    legend: {
      show: !hideExporterLegend,
    },
    tooltip: {
      shared: false,
      intersect: true,
      followCursor: true,
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        const unit = priceView === 'kg' ? '/kg' : `/${boxSize}kg box`;
        const currency = selectedCurrency === 'USD' ? 'USD' : 'RMB';

        // Check if this is a box plot series (market data)
        if (seriesIndex === 0) {
          const priceData = priceView === 'kg' ? data.y : data.yBox;
          const userExporterPrice =
            priceView === 'kg' ? data.userExporterAvg : data.userExporterAvgBox;
          const percentileFormatted =
            data.userExporterPercentile !== null
              ? data.userExporterPercentile.toFixed(0)
              : 'N/A';

          return `
            <div style="${tooltipContainerStyle(darkMode)}">
              <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 2px;">
                ${t('Calibre')} ${data.x}
              </div>
              <div style="font-size: 12px; text-align: center; color: ${chartColors.tooltipText}; margin-bottom: 4px;">
                ${data.totalRecords} ${t('registros')}
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: ${chartColors.tooltipText};">${t('Máximo')}:</span>
                <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${priceData[4].toFixed(2)} ${currency}${unit}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: ${chartColors.tooltipText};">${t('Mediana')}:</span>
                <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${priceData[2].toFixed(2)} ${currency}${unit}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="color: ${chartColors.tooltipText};">${t('Mínimo')}:</span>
                <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${priceData[0].toFixed(2)} ${currency}${unit}</span>
              </div>
              ${
                data.separationType === 'brand' &&
                data.brandsData &&
                data.brandsData.length > 0
                  ? `
                  <div style="margin-top: 8px; border-top: 1px solid ${chartColors.tooltipBorder}; padding-top: 8px;">
                    <div style="font-size: 14px; font-weight: bold; color: ${chartColors.tooltipTitle}; margin-bottom: 6px; text-align: center;">
                      ${t('Etiquetas seleccionadas')}
                    </div>
                    ${data.brandsData
                      .map((brandData, index) => {
                        const brandColor =
                          brandColors[index % brandColors.length];
                        return brandData.avg !== null
                          ? `
                          <div style="margin-bottom: 6px; border-left: 3px solid ${brandColor}; padding-left: 8px;">
                            <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                              <span style="color: ${brandColor}; font-weight: bold;">${brandData.brand}</span>
                              <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${brandData.avg.toFixed(2)} ${currency}${unit}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; font-size: 12px; color: ${chartColors.tooltipText};">
                              <span>${t('Percentil')}: ${brandData.percentile !== null ? brandData.percentile.toFixed(0) : 'N/A'}%</span>
                              <span>${brandData.records} ${t('registros')}</span>
                            </div>
                          </div>
                        `
                          : `
                          <div style="margin-bottom: 6px; border-left: 3px solid ${brandColor}; padding-left: 8px;">
                            <div style="font-size: 12px; color: ${chartColors.tooltipText};">
                              ${brandData.brand}: ${t('No hay datos para este calibre')}
                            </div>
                          </div>
                        `;
                      })
                      .join('')}
                  </div>
                `
                  : ''
              }
              ${
                userExporterPrice !== null && data.separationType !== 'brand'
                  ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #4CAF50; font-weight: bold;">${userExporter?.excel_name} - ${t('Percentil')} ${percentileFormatted}%</span>
                    <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${userExporterPrice.toFixed(2)} ${currency}${unit}</span>
                  </div>
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: left;">
                    ${data.userExporterRecords} ${t('registros')} ${t('de')} ${userExporter?.excel_name}
                  </div>
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: right;">
                    ${userExporter?.excel_name} ${t('está sobre el')} ${percentileFormatted}% ${t('de los datos para este calibre')}
                  </div>
                `
                  : data.separationType !== 'brand'
                    ? `
                    <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                      ${t('No hay datos de')} ${userExporter?.excel_name} ${t('para este calibre')}
                    </div>
                  `
                    : ''
              }
              ${
                data.exportersData && data.exportersData.length > 0
                  ? `
                  <div style="margin-top: 8px; border-top: 1px solid ${chartColors.tooltipBorder}; padding-top: 8px;">
                    <div style="font-size: 14px; font-weight: bold; color: ${chartColors.tooltipTitle}; margin-bottom: 6px; text-align: center;">
                      ${t('Exportadoras Seleccionadas')}
                    </div>
                    ${
                      data.exportersData.some((e) => e.y !== null)
                        ? data.exportersData
                            .filter((e) => e.y !== null)
                            .map(
                              (e) => `
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span style="color: ${e.color}; font-weight: bold;">${e.name}</span>
                          <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${e.y.toFixed(2)} ${currency}${unit}</span>
                        </div>
                      `
                            )
                            .join('')
                        : `
                        <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                          ${t('No hay datos para este calibre')}
                        </div>
                      `
                    }
                  </div>
                `
                  : ''
              }
            </div>
          `;
        }

        // Brand series tooltip
        if (data.brandName) {
          const brandPrice = data.y;
          const brandPercentile =
            data.brandPercentile !== null
              ? data.brandPercentile.toFixed(0)
              : 'N/A';
          const brandRecords = data.brandRecords || 0;

          return `
            <div style="${tooltipContainerStyle(darkMode)}">
              <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 2px;">
                ${data.brandName}
              </div>
              <div style="text-align: center; font-size: 14px; color: ${chartColors.tooltipText}; margin-bottom: 8px;">
                ${t('Calibre')} ${data.x}
              </div>
              ${
                brandPrice !== null
                  ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: ${chartColors.tooltipText};">${t('Precio')}:</span>
                    <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${brandPrice.toFixed(2)} ${currency}${unit}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: ${chartColors.tooltipText};">${t('Percentil')}:</span>
                    <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${brandPercentile}%</span>
                  </div>
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                    ${brandRecords} ${t('registros')}
                  </div>
                `
                  : `
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                    ${t('No hay datos para este calibre')}
                  </div>
                `
              }
            </div>
          `;
        }

        // Bitácora series tooltip
        if (data.isBitacora) {
          return renderBitacoraTooltip({
            point: data,
            categoryLabel: `${t('Calibre')} ${data.x}`,
            currency,
            unit,
            darkMode,
            t,
          });
        }

        // User exporter or top exporters series (when separationType === 'none')
        const price = data.y;
        const percentile = data.userExporterPercentile || data.percentile;
        const records = data.records;
        const seriesName = w.globals.seriesNames[seriesIndex];

        return `
          <div style="${tooltipContainerStyle(darkMode)}">
            <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 2px;">
              ${seriesName}
            </div>
            <div style="text-align: center; font-size: 14px; color: ${chartColors.tooltipText}; margin-bottom: 8px;">
              ${t('Calibre')} ${data.x}
            </div>
            ${
              price !== null && price !== undefined
                ? `
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                  <span style="color: ${chartColors.tooltipText};">${t('Precio')}:</span>
                  <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${price.toFixed(2)} ${currency}${unit}</span>
                </div>
                ${
                  percentile !== null && percentile !== undefined
                    ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: ${chartColors.tooltipText};">${t('Percentil')}:</span>
                      <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${percentile.toFixed(0)}%</span>
                    </div>
                  `
                    : ''
                }
                ${
                  records !== null && records !== undefined
                    ? `
                    <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                      ${records} ${t('registros')}
                    </div>
                  `
                    : ''
                }
              `
                : `
                <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                  ${t('No hay datos para este calibre')}
                </div>
              `
            }
          </div>
        `;
      },
    },
    stroke: {
      colors: [darkMode ? '#ffffff' : '#000000'],
    },
    plotOptions: {
      boxPlot: {
        colors: {
          upper: COLORS.cherry,
          lower: COLORS.cherry,
        },
      },
    },
  };

  return (
    <div>
      <div className='flex flex-col md:flex-row justify-between items-start md:items-center mt-4 mx-4 gap-4'>
        <div className='flex flex-col'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-bold'>{t('Dispersión por calibre')}</h3>
            {visualization === 'chart' && (
              <ChartDownloadButton
                chartRef={chartContainerRef as React.RefObject<HTMLDivElement>}
                title={t('Dispersión por calibre')}
                subtitle={`${selectedVariety} · ${filteredData.length} ${t('registros')}`}
              />
            )}
          </div>
          <span className='text-sm text-default-500 '>
            {filteredData.length} {t('registros')}
          </span>
        </div>

        <div className='flex gap-2 items-center'>
          {visualization === 'table' && categories.length > 0 && (
            <Button
              color='success'
              onClick={exportToExcel}
              startContent={
                <Icon icon='mdi:microsoft-excel' className='text-xl' />
              }
              className='h-12'
            >
              {t('Exportar a Excel')}
            </Button>
          )}
          <VarietyFilter
            data={data}
            selectedVariety={selectedVariety}
            onSelectionChange={(value) => setSelectedVariety(value[0])}
            hideAllOption={true}
            allowAllVarieties={true}
            className='w-full w-48'
          />
        </div>
      </div>
      {visualization === 'chart' ? (
        <div ref={chartContainerRef}>
          <ReactApexChart
            options={chartOptions}
            series={series.map((s) => ({
              ...s,
              data: s.data.map((d) => ({
                ...d,
              })),
            }))}
            type='boxPlot'
            height={400}
          />
        </div>
      ) : (
        <TableView />
      )}

      {/* Brand Detail Modal */}
      {userExporter && (
        <BrandDetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={filteredData}
          caliber={selectedCaliber}
          exporterName={userExporter.excel_name}
          selectedCurrency={selectedCurrency}
          priceView={priceView}
          boxSize={boxSize}
        />
      )}

    </div>
  );
};

export default DailyMarketChart;
